import type { PayloadAction } from '@reduxjs/toolkit';
import { AddPayeeToBenefitAdjustmentRequestBody } from '@/domains/payments/features/paymentModals/modals/payee/types/requests/AddPayeeToBenefitAdjustmentRequestBody';
import { AddPayeeToPaymentAdjustmentRequestBody } from '@/domains/payments/features/paymentModals/modals/payee/types/requests/AddPayeeToPaymentAdjustmentRequestBody';
import { AddPayeeToPaymentRequestBody } from '@/domains/payments/features/paymentModals/modals/payee/types/requests/AddPayeeToPaymentRequestBody';
import { call, put, takeLatest } from 'redux-saga/effects';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { getBenefitSectionDetails } from '@/domains/claims/features/ClaimSetClaimList/store/claimSetClaimList';
import { getPayments } from '@/domains/payments/features/PaymentsSection/store/payments.slice';
import {
  addPayeeToBenefitAdjustment,
  addPayeeToPayment,
  addPayeeToPaymentAdjustment,
} from '@/domains/payments/features/paymentModals/modals/payee/store/addPayee.slice';
import PaymentFormEvent from '@/domains/payments/features/paymentModals/shared/events';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import SnacksService from '@/services/SnackService/SnackService';

function* addPayeeToPaymentSaga({ payload }: PayloadAction<AddPayeeToPaymentRequestBody & { claimId: number }>) {
  const { claimId, ...body } = payload;

  try {
    yield call(HttpService.post, '/Payment/add-payee', body, {
      baseURL: import.meta.env.VITE_API_URL,
    });

    yield put(getPayments({ claimId }));

    SnacksService.add({
      type: SnackType.Success,
      title: 'Payee has been added successfully',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  } catch (error) {
    console.error(error);
  } finally {
    ModalService.close(Modals.AddPayeeToPayment);
    EventService.dispatch(new PaymentFormEvent.CreateRequestSuccess({}));
  }
}

function* addPayeeToPaymentAdjustmentSaga({
  payload,
}: PayloadAction<AddPayeeToPaymentAdjustmentRequestBody & { claimId: number }>) {
  try {
    const { claimId, ...body } = payload;

    yield call(HttpService.post, '/PaymentAdjustment/add-payee', body, {
      baseURL: import.meta.env.VITE_API_URL,
    });

    yield put(getPayments({ claimId }));

    SnacksService.add({
      type: SnackType.Success,
      title: 'Payee has been added successfully',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  } catch (error) {
    console.error(error);
  } finally {
    ModalService.close(Modals.AddPayeeToPaymentAdjustment);
    EventService.dispatch(new PaymentFormEvent.CreateRequestSuccess({}));
  }
}

function* addPayeeToBenefitAdjustmentSaga({
  payload,
}: PayloadAction<AddPayeeToBenefitAdjustmentRequestBody & { claimId: number }>) {
  try {
    const { claimId, ...body } = payload;

    yield call(HttpService.post, '/examiner/benefit-adjustment/add-payee', body, {
      baseURL: import.meta.env.VITE_API_URL_V2,
    });

    yield put(getBenefitSectionDetails(String(claimId)));

    SnacksService.add({
      type: SnackType.Success,
      title: 'Payee has been added successfully',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  } catch (error) {
    console.error(error);
  } finally {
    ModalService.close(Modals.AddPayeeToBenefitAdjustment);
    EventService.dispatch(new PaymentFormEvent.CreateRequestSuccess({}));
  }
}

export function* addPayeeWatcher() {
  yield takeLatest([addPayeeToPayment.type], addPayeeToPaymentSaga);
  yield takeLatest([addPayeeToPaymentAdjustment.type], addPayeeToPaymentAdjustmentSaga);
  yield takeLatest([addPayeeToBenefitAdjustment.type], addPayeeToBenefitAdjustmentSaga);
}
